import React, { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordValidator } from 'record-it-utils/password/PasswordValidator/PasswordValidator';
import {
  ButtonStandard,
  ButtonStandardHoverEffectType,
  ButtonStandardType
} from '../../button/ButtonStandard/ButtonStandard';
import { CardBody } from '../../card/CardBody/CardBody';
import {
  CardHeader,
  HorizontalAlignment
} from '../../card/CardHeader/CardHeader';
import {
  CardStandard,
  CardStandardProps
} from '../../card/CardStandard/CardStandard';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useNotificationManager } from '../../hooks/useNotificationManager';
import { TextInput, TextInputType } from '../../input/TextInput/TextInput';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { PasswordRulesBox } from '../../misc/PasswordRulesBox/PasswordRulesBox';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { FormGroup } from '../FormGroup/FormGroup';
import styles from './ResetPasswordForm.module.css';

export type ResetPasswordFormProps = PropsWithoutChildren<CardStandardProps> & {
  resetPassword: (password: string) => Promise<void>;
  onCancel: () => void;
  onFinish: () => void;
};

export const ResetPasswordForm: FunctionComponent<ResetPasswordFormProps> =
  React.memo((props) => {
    const { className, resetPassword, onCancel, onFinish, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });

    const [password, setPassword] = useState('');
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [resettingPasswordFailed, setResettingPasswordFailed] =
      useState(false);

    const { t } = useTranslation('record-it-components');
    const notificationManager = useNotificationManager();

    const handleResetPasswordButtonClick = useEventCallback(() => {
      if (isResettingPassword) {
        return;
      }

      setIsResettingPassword(true);
      setResettingPasswordFailed(false);

      void resetPassword(password)
        .then(() => {
          notificationManager.success({
            title: t('form.ResetPasswordForm.successNotification.title'),
            message: t('form.ResetPasswordForm.successNotification.message')
          });
          onFinish();
        })
        .catch((e) => {
          console.error(e);
          setResettingPasswordFailed(true);
        })
        .finally(() => {
          setIsResettingPassword(false);
        });
    });

    const passwordValidity = useMemo(() => {
      const validator = new PasswordValidator();
      return validator.validate(password);
    }, [password]);

    return (
      <CardStandard
        {...remainingProps}
        className={classes(className, styles.card)}
      >
        <CardHeader textAlignment={HorizontalAlignment.Center}>
          {t('form.ResetPasswordForm.title')}
        </CardHeader>
        <CardBody>
          {resettingPasswordFailed ? (
            <MessageBox
              type={MessageBoxType.Error}
              className={styles.resettingPasswordErrorMessageBox}
              data-testid="ResetPasswordForm.ResettingPasswordFailedText"
            >
              {t('form.ResetPasswordForm.resettingPasswordFailedText')}
            </MessageBox>
          ) : null}

          <FormGroup>
            <TextInput
              label={t('form.ResetPasswordForm.passwordLabel')}
              type={TextInputType.Password}
              value={password}
              onInput={setPassword}
              data-testid="ResetPasswordForm.PasswordTextInput"
            ></TextInput>
            <PasswordRulesBox
              passwordValidity={passwordValidity}
              className={styles.passwordRuleBox}
            ></PasswordRulesBox>
          </FormGroup>

          <div className={styles.resetPasswordButtonWrapper}>
            <ButtonStandard
              type={ButtonStandardType.Primary}
              onClick={handleResetPasswordButtonClick}
              disabled={!passwordValidity.valid}
              data-testid="ResetPasswordForm.ResetPasswordButton"
            >
              {t('form.ResetPasswordForm.resetPassword')}
            </ButtonStandard>
          </div>

          <div className={styles.cancelButtonWrapper}>
            <ButtonStandard
              type={ButtonStandardType.TextSecondary}
              hoverEffectType={ButtonStandardHoverEffectType.HalfTransparent}
              onClick={onCancel}
              data-testid="ResetPasswordForm.CancelButton"
            >
              {t('form.ForgotPasswordForm.cancel')}
            </ButtonStandard>
          </div>

          <LoadingOverlay
            active={isResettingPassword}
            data-testid="ResetPasswordForm.LoadingOverlay"
          ></LoadingOverlay>
        </CardBody>
      </CardStandard>
    );
  });
